import { computed, onUnmounted, ref, watch } from 'vue';

/**
 * Dynamic "thinking..." status label for the in-flight agent turn.
 * Shared between rm-portal (Vue 2.7) and rm-portal-beta (Vue 3).
 *
 * In REST-only transport the loop is opaque between request and response,
 * so we rotate through timed fallback phrases. In socket mode — the
 * default — tool_start events push the running tool name into the
 * composable, and the label becomes a contextual "what Atlas is doing
 * right now" phrase ("Searching jobs…", "Running Content Generation
 * chain…"). Tool input is also passed where available so labels can
 * reference specifics ("Reading 3 documents", not just "Reading").
 *
 * @param {Ref<boolean>} isActive              — true while a turn is in flight
 * @param {Ref<string|null>} [mostRecentToolName] — running tool name
 * @param {Ref<any>} [mostRecentToolInput]     — input the tool was invoked with
 */
export function thinkingStatus(
    isActive,
    mostRecentToolName = ref(null),
    mostRecentToolInput = ref(null),
) {
    const TIMED_LABELS = [
        { age: 0, label: 'Thinking' },
        { age: 2, label: 'Exploring your architecture' },
        { age: 5, label: 'Reading job details' },
        { age: 9, label: 'Piecing it together' },
        { age: 14, label: 'Still working' },
        { age: 25, label: 'This is taking a moment' },
    ];

    /**
     * Map each tool to either:
     *  - a static string label, or
     *  - a function `(input) => string` that can reference the tool's
     *    input to produce a more specific phrase (e.g. "Running Content
     *    Generation chain" vs generic "Running prompt chain").
     * Falls back to the tool name with underscores spaced out when a
     * tool isn't listed here.
     */
    const TOOL_LABELS = {
        list_job_hierarchies: 'Reading your hierarchies',
        search_jobs_by_name: (input) =>
            input?.name
                ? `Searching jobs for "${_truncate(String(input.name), 40)}"`
                : 'Searching jobs',
        get_job_details: (input) => {
            const ids = Array.isArray(input?.node_ids) ? input.node_ids : [];
            if (ids.length > 1) return `Reading ${ids.length} job details`;
            return 'Reading job details';
        },
        get_job_by_reference: (input) =>
            input?.reference
                ? `Looking up ${_truncate(String(input.reference), 24)}`
                : 'Looking up the reference',
        list_prompt_chains: 'Checking available automations',
        run_prompt_chain: (input) =>
            input?.chain_id
                ? `Kicking off prompt chain #${input.chain_id}`
                : 'Kicking off a prompt chain',
    };

    const elapsedMs = ref(0);
    const startedAt = ref(null);
    let timer = null;

    function start() {
        startedAt.value = Date.now();
        elapsedMs.value = 0;
        stopTimer();
        timer = setInterval(() => {
            if (startedAt.value !== null) {
                elapsedMs.value = Date.now() - startedAt.value;
            }
        }, 250);
    }

    function stopTimer() {
        if (timer) {
            clearInterval(timer);
            timer = null;
        }
    }

    function reset() {
        stopTimer();
        startedAt.value = null;
        elapsedMs.value = 0;
    }

    watch(
        isActive,
        (active) => {
            if (active) start();
            else reset();
        },
        { immediate: true },
    );

    onUnmounted(stopTimer);

    const elapsedSeconds = computed(() => Math.floor(elapsedMs.value / 1000));

    const timeBasedLabel = computed(() => {
        const seconds = elapsedSeconds.value;
        let chosen = TIMED_LABELS[0].label;
        for (const entry of TIMED_LABELS) {
            if (seconds >= entry.age) chosen = entry.label;
        }
        return chosen;
    });

    const label = computed(() => {
        const toolName = mostRecentToolName.value;
        if (toolName) {
            const entry = TOOL_LABELS[toolName];
            if (typeof entry === 'function') {
                try {
                    return entry(mostRecentToolInput.value);
                } catch {
                    // Fall through to static / fallback label below.
                }
            }
            if (typeof entry === 'string') return entry;
            return toolName.replace(/_/g, ' ');
        }
        return timeBasedLabel.value;
    });

    const display = computed(() => {
        const s = elapsedSeconds.value;
        return s >= 2 ? `${label.value} · ${s}s` : label.value;
    });

    return { label, display, elapsedSeconds };
}

function _truncate(s, max) {
    if (typeof s !== 'string') return '';
    return s.length > max ? s.slice(0, max - 1) + '…' : s;
}
